import {
  dueTimeOf,
  hasChildren,
  isoWeek,
  nextOccurrence,
  parseYmd,
  repeatPeriod,
  sprintStart,
} from '@nedelnik/shared';

import { haptic } from '../haptic.js';
import { Check, Repeat as RepeatIcon } from '../icons.js';
import { useMenu } from '../menu.js';

import type { Repeat, SprintKey, SprintType, Task } from '@nedelnik/shared';

/**
 * Повтор задачи — кнопка ↻ рядом с полем спринта (3.19.1).
 *
 * Рядом со спринтом, а не со сроком: повтор — это «копия в каждом новом
 * спринте», и от даты он не зависит. Кнопка — тот же квадрат, что «убрать
 * срок», меню — то же, что у спринта: в карточке нет ни одного элемента,
 * которого не было раньше. Включённый повтор — бежевая подложка выбранного
 * сегмента («Бэклог»).
 *
 * Четвёртый вариант за день, и три прошлых владелец отверг (2026-10-18):
 * серое слово «↻ повторять» не нашли, поле с переключателем во всю ширину
 * вышло крупным, галочка под датой выбивалась из карточки, где всё остальное
 * — поля и кнопки в рамке.
 */

/** Предложный падеж: «в ноябре». `Intl` его не знает, а падежей всего двенадцать. */
const MONTHS_IN = [
  'январе',
  'феврале',
  'марте',
  'апреле',
  'мае',
  'июне',
  'июле',
  'августе',
  'сентябре',
  'октябре',
  'ноябре',
  'декабре',
];

const DAY = new Intl.DateTimeFormat('ru-RU', { weekday: 'short', day: 'numeric', month: 'short' });

const LABELS: Record<Repeat, string> = {
  week: 'Каждую неделю',
  month: 'Каждый месяц',
};

/** «в неделе 44», «в ноябре» — куда попадёт экземпляр. */
function inPeriod(sprint: SprintKey): string {
  const start = sprintStart(sprint);
  if (sprint.startsWith('w:')) return `в неделе ${String(isoWeek(start))}`;
  return `в ${MONTHS_IN[start.getMonth()] ?? ''}`;
}

/**
 * Есть ли у задачи повтор как выбор. Во Входящих повторять некуда, у проекта
 * неясно, что делать с вложенными (3.19.7), а прошлый экземпляр серии
 * выглядит обычной задачей: включённый у старой копии повтор завёл бы вторую
 * голову серии и дубль в текущем спринте (3.19.6).
 */
export function repeatAvailable(task: Task, tasks: readonly Task[]): boolean {
  if (task.sprint === null || hasChildren(task.id, tasks)) return false;
  return !(task.repeat == null && task.series_id != null);
}

/** Повтор, как его видит человек: чаще спринта он не срабатывает (3.19.2). */
function shownRepeat(task: Task, sprintType: SprintType): Repeat | null {
  return task.repeat == null ? null : repeatPeriod(task.repeat, sprintType);
}

export function RepeatMenu({
  task,
  sprintType,
  onChange,
}: {
  task: Task;
  sprintType: SprintType;
  onChange: (repeat: Repeat | null) => void;
}): React.ReactElement {
  const menu = useMenu();
  const current = shownRepeat(task, sprintType);
  const options: (Repeat | null)[] =
    sprintType === 'week' ? [null, 'week', 'month'] : [null, 'month'];
  const label = current === null ? 'Повторять задачу' : `Повтор: ${LABELS[current].toLowerCase()}`;

  return (
    <span className="menu" ref={menu.box}>
      <button
        type="button"
        className={`iconbtn repeatbtn${current === null ? '' : ' repeatbtn--on'}`}
        aria-label={label}
        title={label}
        aria-haspopup="menu"
        aria-expanded={menu.open}
        onClick={menu.toggle}
      >
        <RepeatIcon />
      </button>

      {menu.open && (
        <span className="menu__list" role="menu" aria-label="Повтор задачи">
          {options.map((option) => (
            <button
              key={option ?? 'none'}
              type="button"
              role="menuitemradio"
              aria-checked={current === option}
              className="menu__item"
              onClick={() => {
                if (current !== option) {
                  haptic();
                  onChange(option);
                }
                menu.close();
              }}
            >
              {option === null ? 'Не повторять' : LABELS[option]}
              {current === option && <Check />}
            </button>
          ))}
        </span>
      )}
    </span>
  );
}

/** Под строкой спринта: какой повтор и когда появится следующая копия. */
export function RepeatNext({
  task,
  sprintType,
}: {
  task: Task;
  sprintType: SprintType;
}): React.ReactElement | null {
  const current = shownRepeat(task, sprintType);
  if (current === null || task.repeat == null || task.sprint === null) return null;

  const next = nextOccurrence(
    { sprint: task.sprint, due: task.due, repeat: task.repeat },
    sprintType,
  );
  const time = next.due === null ? null : dueTimeOf({ due: next.due, due_time: task.due_time });
  const when =
    next.due === null
      ? inPeriod(next.sprint)
      : `${DAY.format(parseYmd(next.due))}${time === null ? '' : `, ${time}`}`;

  return (
    <p className="repeat__next">
      {LABELS[current]} · следующая — {when}
    </p>
  );
}
